import React from 'react';
import { ISessionConversionWithDaily } from '../../store/types/sessionConversion';
import { Paper, Typography, Box, Grid, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
} from 'chart.js';

ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title
);

interface Props {
    stats: ISessionConversionWithDaily;
}

const SessionConversionStats: React.FC<Props> = ({ stats }) => {
    const theme = useTheme();
    const session = stats.aggregate.sessionStats[0];
    const dailyStats = stats.dailyStats;
    
    // Calculate conversion percentages
    const totalSessions = session.totalSessions;
    const sessionsWithEvents = parseInt(session.sessionsWithEvents);
    const sessionsWithoutEvents = parseInt(session.sessionsWithoutEvents);
    const sessionsWithAddToCart = parseInt(session.sessionsWithAddToCart);
    const sessionsWithOrders = parseInt(session.sessionsWithOrders);
    const uniqueCustomers = session.uniqueCustomers;

    // Calculate percentages
    const eventsToSessionsPct = totalSessions > 0 ? ((sessionsWithEvents / totalSessions) * 100).toFixed(2) : '0';
    const addToCartPct = totalSessions > 0 ? ((sessionsWithAddToCart / totalSessions) * 100).toFixed(2) : '0';
    const ordersPct = totalSessions > 0 ? ((sessionsWithOrders / totalSessions) * 100).toFixed(2) : '0';
    const addToCartToEventsPct = sessionsWithEvents > 0 ? ((sessionsWithAddToCart / sessionsWithEvents) * 100).toFixed(2) : '0';
    const ordersToAddToCartPct = sessionsWithAddToCart > 0 ? ((sessionsWithOrders / sessionsWithAddToCart) * 100).toFixed(2) : '0';

    // Determine highlight colors based on conversion rates
    const getHighlight = (type: string, value: number) => {
        const isDark = theme.palette.mode === 'dark';
        if (type === 'conversion') {
            if (value > 20) return isDark ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9'; // light green
            if (value > 10) return isDark ? 'rgba(255, 253, 231, 0.2)' : '#fffde7'; // light yellow
            return isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee'; // light red
        }
        if (type === 'addToCart') {
            if (value > 50) return isDark ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9';
            if (value > 30) return isDark ? 'rgba(255, 253, 231, 0.2)' : '#fffde7';
            return isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee';
        }
        if (type === 'checkout') {
            if (value > 35) return isDark ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9';
            if (value > 20) return isDark ? 'rgba(255, 253, 231, 0.2)' : '#fffde7';
            return isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee';
        }
        return theme.palette.background.paper;
    };

    // Main metrics
    const metrics = [
        {
            value: totalSessions.toLocaleString(),
            label: 'Total Sessions',
            highlight: theme.palette.background.paper,
        },
        {
            value: (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {sessionsWithEvents.toLocaleString()}
                    <span style={{ fontSize: 18, color: theme.palette.text.secondary, marginLeft: 6 }}>
                        ({eventsToSessionsPct}%)
                    </span>
                </span>
            ),
            label: 'Sessions with Events',
            highlight: theme.palette.background.paper,
        },
        {
            value: sessionsWithoutEvents.toLocaleString(),
            label: 'Sessions without Events',
            highlight: theme.palette.background.paper,
        },
        {
            value: uniqueCustomers.toLocaleString(),
            label: 'Unique Customers',
            highlight: theme.palette.background.paper,
        },
    ];

    const conversionMetrics = [
        {
            value: (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {sessionsWithAddToCart.toLocaleString()}
                    <span style={{ fontSize: 18, color: theme.palette.text.secondary, marginLeft: 6 }}>
                        ({addToCartPct}%)
                    </span>
                </span>
            ),
            label: 'Add to Cart',
            sub: (
                <span style={{ fontSize: 15, color: theme.palette.text.secondary }}>
                    {addToCartToEventsPct}% of sessions with events
                </span>
            ),
            highlight: getHighlight('addToCart', parseFloat(addToCartToEventsPct)),
        },
        {
            value: (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {sessionsWithOrders.toLocaleString()}
                    <span style={{ fontSize: 18, color: theme.palette.text.secondary, marginLeft: 6 }}>
                        ({ordersPct}%)
                    </span>
                </span>
            ),
            label: 'Sessions with Orders',
            sub: (
                <span style={{ fontSize: 15, color: theme.palette.text.secondary }}>
                    {ordersToAddToCartPct}% of add to cart sessions
                </span>
            ),
            highlight: getHighlight('checkout', parseFloat(ordersToAddToCartPct)),
        },
    ];

    // Pie chart: Sessions with/without events
    const sessionsPieData = {
        labels: ['With Events', 'Without Events'],
        datasets: [{
            data: [sessionsWithEvents, sessionsWithoutEvents],
            backgroundColor: ['#4caf50', '#e0e0e0'],
        }]
    };

    // Pie chart: Conversion funnel
    const conversionPieData = {
        labels: ['Sessions with Events', 'Add to Cart', 'Orders'],
        datasets: [{
            data: [sessionsWithEvents - sessionsWithAddToCart, sessionsWithAddToCart - sessionsWithOrders, sessionsWithOrders],
            backgroundColor: ['#2196f3', '#ff9800', '#4caf50'],
        }]
    };

    return (
        <Box>
            {/* Key metrics */}
            <Typography variant="h6" sx={{ mb: 2 }}>Overview</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }} justifyContent="center" alignItems="stretch" flexWrap="wrap">
                {metrics.map((m) => (
                    <Grid item xs={12} sm={6} md={3} key={m.label}>
                        <Paper sx={{ p: 2, minHeight: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 2, boxShadow: 2, backgroundColor: m.highlight }}>
                            <Typography sx={{ fontSize: 32, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{m.value}</Typography>
                            <Typography sx={{ fontSize: 14, color: theme.palette.text.secondary, mt: 1 }}>{m.label}</Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Conversion metrics */}
            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>Conversion Funnel</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }} justifyContent="center" alignItems="stretch" flexWrap="wrap">
                {conversionMetrics.map((m) => (
                    <Grid item xs={12} sm={6} md={6} key={m.label}>
                        <Paper sx={{ p: 2, minHeight: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 2, boxShadow: 2, backgroundColor: m.highlight }}>
                            <Typography sx={{ fontSize: 32, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{m.value}</Typography>
                            {m.sub && <Typography sx={{ fontSize: 15, color: theme.palette.text.secondary, mt: 1 }}>{m.sub}</Typography>}
                            <Typography sx={{ fontSize: 14, color: theme.palette.text.secondary, mt: 1, fontWeight: 600 }}>{m.label}</Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Daily Trend Charts - Only show if multiple days selected */}
            {dailyStats && dailyStats.length > 1 && (
                <>
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Daily Trends</Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
                                    Sessions Overview
                                </Typography>
                                <Line 
                                    data={{
                                        labels: dailyStats.map(d => d.date),
                                        datasets: [
                                            {
                                                label: 'Total Sessions',
                                                data: dailyStats.map(d => d.totalSessions),
                                                borderColor: '#2196f3',
                                                backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                                tension: 0.3,
                                                fill: true,
                                            },
                                            {
                                                label: 'Sessions with Events',
                                                data: dailyStats.map(d => d.sessionsWithEvents),
                                                borderColor: '#00bcd4',
                                                backgroundColor: 'rgba(0, 188, 212, 0.2)',
                                                tension: 0.3,
                                                fill: true,
                                            }
                                        ]
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: true,
                                        plugins: {
                                            legend: {
                                                position: 'top' as const,
                                            },
                                            tooltip: {
                                                mode: 'index',
                                                intersect: false,
                                                callbacks: {
                                                    label: function(context) {
                                                        const label = context.dataset.label || '';
                                                        const value = context.parsed.y;
                                                        const dataIndex = context.dataIndex;
                                                        const totalSessions = dailyStats[dataIndex].totalSessions;
                                                        
                                                        if (label === 'Sessions with Events' && totalSessions > 0) {
                                                            const percentage = ((value / totalSessions) * 100).toFixed(2);
                                                            return `${label}: ${value.toLocaleString()} (${percentage}%)`;
                                                        }
                                                        return `${label}: ${value.toLocaleString()}`;
                                                    }
                                                }
                                            }
                                        },
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                ticks: {
                                                    precision: 0
                                                }
                                            }
                                        }
                                    }}
                                />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
                                    Conversions
                                </Typography>
                                <Line 
                                    data={{
                                        labels: dailyStats.map(d => d.date),
                                        datasets: [
                                            {
                                                label: 'Add to Cart',
                                                data: dailyStats.map(d => d.sessionsWithAddToCart),
                                                borderColor: '#ff9800',
                                                backgroundColor: 'rgba(255, 152, 0, 0.2)',
                                                tension: 0.3,
                                                fill: true,
                                            },
                                            {
                                                label: 'Orders',
                                                data: dailyStats.map(d => d.sessionsWithOrders),
                                                borderColor: '#4caf50',
                                                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                                tension: 0.3,
                                                fill: true,
                                            }
                                        ]
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: true,
                                        plugins: {
                                            legend: {
                                                position: 'top' as const,
                                            },
                                            tooltip: {
                                                mode: 'index',
                                                intersect: false,
                                                callbacks: {
                                                    label: function(context) {
                                                        const label = context.dataset.label || '';
                                                        const value = context.parsed.y;
                                                        const dataIndex = context.dataIndex;
                                                        const totalSessions = dailyStats[dataIndex].totalSessions;
                                                        const addToCart = dailyStats[dataIndex].sessionsWithAddToCart;
                                                        
                                                        if (label === 'Add to Cart' && totalSessions > 0) {
                                                            const percentage = ((value / totalSessions) * 100).toFixed(2);
                                                            return `${label}: ${value.toLocaleString()} (${percentage}% of sessions)`;
                                                        }
                                                        if (label === 'Orders') {
                                                            const percentageOfSessions = totalSessions > 0 
                                                                ? ((value / totalSessions) * 100).toFixed(2)
                                                                : '0';
                                                            const percentageOfCart = addToCart > 0 
                                                                ? ((value / addToCart) * 100).toFixed(2)
                                                                : '0';
                                                            return `${label}: ${value.toLocaleString()} (${percentageOfSessions}% of sessions, ${percentageOfCart}% of cart)`;
                                                        }
                                                        return `${label}: ${value.toLocaleString()}`;
                                                    }
                                                }
                                            }
                                        },
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                ticks: {
                                                    precision: 0
                                                }
                                            }
                                        }
                                    }}
                                />
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Daily Data Table */}
                    <Paper sx={{ p: 3, mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                            Daily Breakdown
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Total Sessions</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Sessions with Events</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Add to Cart</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Events→Cart Conv.</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Orders</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Cart→Order Conv.</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dailyStats.map((day) => {
                                        const eventsPercent = day.totalSessions > 0 
                                            ? ((day.sessionsWithEvents / day.totalSessions) * 100).toFixed(2)
                                            : '0.00';
                                        const addToCartPercent = day.totalSessions > 0 
                                            ? ((day.sessionsWithAddToCart / day.totalSessions) * 100).toFixed(2)
                                            : '0.00';
                                        const ordersPercent = day.totalSessions > 0 
                                            ? ((day.sessionsWithOrders / day.totalSessions) * 100).toFixed(2)
                                            : '0.00';
                                        const eventsToCartPercent = day.sessionsWithEvents > 0 
                                            ? ((day.sessionsWithAddToCart / day.sessionsWithEvents) * 100).toFixed(2)
                                            : '0.00';
                                        const cartToOrderPercent = day.sessionsWithAddToCart > 0 
                                            ? ((day.sessionsWithOrders / day.sessionsWithAddToCart) * 100).toFixed(2)
                                            : '0.00';

                                        return (
                                            <TableRow key={day.date} sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                                                <TableCell sx={{ fontWeight: 500 }}>{day.date}</TableCell>
                                                <TableCell align="right">{day.totalSessions.toLocaleString()}</TableCell>
                                                <TableCell align="right">
                                                    {day.sessionsWithEvents.toLocaleString()}
                                                    <Typography component="span" sx={{ fontSize: 12, color: theme.palette.text.secondary, ml: 1 }}>
                                                        ({eventsPercent}%)
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {day.sessionsWithAddToCart.toLocaleString()}
                                                    <Typography component="span" sx={{ fontSize: 12, color: theme.palette.text.secondary, ml: 1 }}>
                                                        ({addToCartPercent}%)
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography component="span" sx={{ 
                                                        fontWeight: 600,
                                                        color: parseFloat(eventsToCartPercent) > 50 ? '#4caf50' : parseFloat(eventsToCartPercent) > 40 ? '#ff9800' : '#f44336'
                                                    }}>
                                                        {eventsToCartPercent}%
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {day.sessionsWithOrders.toLocaleString()}
                                                    <Typography component="span" sx={{ fontSize: 12, color: theme.palette.text.secondary, ml: 1 }}>
                                                        ({ordersPercent}%)
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography component="span" sx={{ 
                                                        fontWeight: 600,
                                                        color: parseFloat(cartToOrderPercent) > 35 ? '#4caf50' : parseFloat(cartToOrderPercent) > 25 ? '#ff9800' : '#f44336'
                                                    }}>
                                                        {cartToOrderPercent}%
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {/* Summary Row */}
                                    <TableRow sx={{ backgroundColor: theme.palette.action.hover, fontWeight: 600 }}>
                                        <TableCell sx={{ fontWeight: 700 }}>Total / Average</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {dailyStats.reduce((sum, day) => sum + day.totalSessions, 0).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {dailyStats.reduce((sum, day) => sum + day.sessionsWithEvents, 0).toLocaleString()}
                                            <Typography component="span" sx={{ fontSize: 12, color: theme.palette.text.secondary, ml: 1 }}>
                                                ({((dailyStats.reduce((sum, day) => sum + day.sessionsWithEvents, 0) / 
                                                    dailyStats.reduce((sum, day) => sum + day.totalSessions, 0)) * 100).toFixed(2)}%)
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {dailyStats.reduce((sum, day) => sum + day.sessionsWithAddToCart, 0).toLocaleString()}
                                            <Typography component="span" sx={{ fontSize: 12, color: theme.palette.text.secondary, ml: 1 }}>
                                                ({((dailyStats.reduce((sum, day) => sum + day.sessionsWithAddToCart, 0) / 
                                                    dailyStats.reduce((sum, day) => sum + day.totalSessions, 0)) * 100).toFixed(2)}%)
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            <Typography component="span" sx={{ 
                                                fontWeight: 700,
                                                color: '#2196f3'
                                            }}>
                                                {((dailyStats.reduce((sum, day) => sum + day.sessionsWithAddToCart, 0) / 
                                                    dailyStats.reduce((sum, day) => sum + day.sessionsWithEvents, 0)) * 100).toFixed(2)}%
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {dailyStats.reduce((sum, day) => sum + day.sessionsWithOrders, 0).toLocaleString()}
                                            <Typography component="span" sx={{ fontSize: 12, color: theme.palette.text.secondary, ml: 1 }}>
                                                ({((dailyStats.reduce((sum, day) => sum + day.sessionsWithOrders, 0) / 
                                                    dailyStats.reduce((sum, day) => sum + day.totalSessions, 0)) * 100).toFixed(2)}%)
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            <Typography component="span" sx={{ 
                                                fontWeight: 700,
                                                color: '#2196f3'
                                            }}>
                                                {((dailyStats.reduce((sum, day) => sum + day.sessionsWithOrders, 0) / 
                                                    dailyStats.reduce((sum, day) => sum + day.sessionsWithAddToCart, 0)) * 100).toFixed(2)}%
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}

            {/* Visualization */}
            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>Analytics</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>Sessions Distribution</Typography>
                        <Pie data={sessionsPieData} />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>Conversion Breakdown</Typography>
                        <Pie data={conversionPieData} />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SessionConversionStats;

